<!--
  Общий компактный workflow ЭДО-документа.
  Статусы, участники и группировку титулов берет из backend document_flow.
-->
<template>
  <div
    v-if="
      flow &&
      (visibleSteps.length || normalizedAdditionalGroups.length)
    "
    class="edo-document-flow-details"
  >
    <div class="edo-document-flow-details__section">
      <EdoDocumentTitleFlowCard
        :eyebrow="eyebrow"
        :title="title"
        :current-label="showCurrentLabel ? flow.current_label : ''"
        :tone="flow.tone || 'neutral'"
        :titles="titleCards"
        :active-title-key="activeTitleKey"
        :expanded="
          Boolean(selectedTitle && !selectedTitle.isAdditional)
        "
        :read-only="readOnly"
        :inspectable="inspectable"
        :variant="variant"
        @select-title="selectTitle"
      />

      <EdoDocumentTitleDetailsPanel
        v-if="selectedTitle && !selectedTitle.isAdditional"
        :group="selectedTitle.group"
        :details="selectedTitle.details"
        :actions="selectedTitleActions"
        :navigation="selectedTitleNavigation"
        :help-items="titleHelpItems"
        @action="emitTitleAction"
        @close="closeInspector"
        @select-title="selectTitle"
      />
    </div>

    <div
      v-if="(!readOnly || inspectable) && additionalTitleCards.length"
      class="edo-document-flow-details__section"
    >
      <EdoDocumentAdditionalTitles
        :items="additionalTitleCards"
        :active-title-key="activeTitleKey"
        :expanded="Boolean(selectedTitle?.isAdditional)"
        :open="additionalTitlesOpen"
        @select-title="selectTitle"
        @update:open="setAdditionalTitlesOpen"
      />

      <EdoDocumentTitleDetailsPanel
        v-if="selectedTitle?.isAdditional"
        :group="selectedTitle.group"
        :details="selectedTitle.details"
        :actions="selectedTitleActions"
        :navigation="selectedTitleNavigation"
        :help-items="titleHelpItems"
        @action="emitTitleAction"
        @close="closeInspector"
        @select-title="selectTitle"
      />
    </div>
  </div>
</template>

<script>
import EdoDocumentAdditionalTitles from "@/components/Edo/shared/EdoDocumentAdditionalTitles.vue";
import EdoDocumentTitleFlowCard from "@/components/Edo/shared/EdoDocumentTitleFlowCard.vue";
import EdoDocumentTitleDetailsPanel from "@/components/Edo/shared/EdoDocumentTitleDetailsPanel.vue";
import {
  edoDocumentFlowParticipantLabel,
  edoDocumentFlowParticipantTimelineLabel,
} from "@/utils/edoDocumentFlowParticipantUtils";
import { filterExistingEdoAdditionalTitleCards } from "@/utils/edoDocumentAdditionalTitleUtils";

const ACTIVE_STATUSES = [
  "error",
  "warning",
  "current",
  "blocked",
  "planned",
  "pending",
];

export default {
  name: "EdoDocumentFlowDetails",

  components: {
    EdoDocumentAdditionalTitles,
    EdoDocumentTitleFlowCard,
    EdoDocumentTitleDetailsPanel,
  },

  emits: ["title-action"],

  props: {
    flow: {
      type: Object,
      default: null,
    },
    title: {
      type: String,
      default: "Информация по этапам",
    },
    eyebrow: {
      type: String,
      default: "Документооборот",
    },
    showCurrentLabel: {
      type: Boolean,
      default: true,
    },
    guides: {
      type: Object,
      default: () => ({}),
    },
    fallbackGuide: {
      type: Object,
      default: () => ({}),
    },
    // Домен передаёт здесь только реальные данные титулов. Состояние процесса
    // всегда берётся из соответствующей backend-группы document_flow.
    titleDetails: {
      type: Array,
      default: () => [],
    },
    additionalTitleDetails: {
      type: Array,
      default: () => [],
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    inspectable: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: "card",
      validator: (value) => ["card", "embedded", "flat"].includes(value),
    },
  },

  data() {
    return {
      selectedTitleKey: null,
      additionalTitlesOpen: false,
      initialSelectionApplied: false,
    };
  },

  watch: {
    normalizedGroups: {
      immediate: true,
      handler(groups) {
        if (
          this.readOnly ||
          this.initialSelectionApplied ||
          !groups.length
        )
          return;

        const initialGroupKey = this.flow?.initial_group_key;
        if (!initialGroupKey) return;

        const initialGroup = groups.find(
          (group) => group.key === initialGroupKey
        );
        if (!initialGroup) return;

        // Backend единообразно выбирает стартовую группу для всех типов ЭДО.
        // Открываем её один раз, чтобы refresh не отменял ручное закрытие панели.
        this.initialSelectionApplied = true;
        this.openTitle(initialGroup);
      },
    },
  },

  computed: {
    visibleSteps() {
      const steps = Array.isArray(this.flow?.steps)
        ? this.flow.steps
        : [];
      return steps.filter((step) => step.table_visible !== false);
    },

    normalizedGroups() {
      const groups = Array.isArray(this.flow?.groups)
        ? this.flow.groups
        : [];

      // stepKeys — локальное имя старого UI-контракта дочерних компонентов.
      // Состав и статус группы при этом полностью остаются данными backend.
      return groups.map((group) => ({
        ...group,
        stepKeys: group.step_keys,
      }));
    },

    normalizedAdditionalGroups() {
      const groups = Array.isArray(this.flow?.additional_groups)
        ? this.flow.additional_groups
        : [];

      // Независимые шаги вложены в саму backend-группу и не смешиваются
      // с последовательностью основных титулов документа.
      return groups.map((group) => ({
        ...group,
        stepKeys: group.step_keys,
        steps: Array.isArray(group.steps) ? group.steps : [],
      }));
    },

    titleCards() {
      return this.normalizedGroups
        .map((group) => {
          const steps = group.stepKeys
            .map((key) => this.stepByKey(key))
            .filter(Boolean);

          return {
            key: group.key,
            label: group.label,
            role:
              group.participant_label ||
              group.participant?.label ||
              "",
            note: this.participantLabel(group, steps),
            status: group.status || "pending",
            tone: group.tone || "neutral",
            stepKeys: group.stepKeys,
            ariaLabel: steps
              .map((step) => this.stepAriaLabel(step))
              .join(". "),
            tooltip: this.groupTooltip(group, steps),
          };
        })
        .filter((group) =>
          group.stepKeys.some((key) => this.stepByKey(key))
        );
    },

    additionalTitleCards() {
      const cards = this.normalizedAdditionalGroups.map((group) => {
        const details = this.additionalTitleDetails.find(
          (item) => item.key === group.key
        );

        return {
          key: group.key,
          label: group.label || details?.label,
          title: details?.title || group.label,
          role:
            group.participant_label ||
            group.participant?.label ||
            details?.role ||
            "",
          description: details?.description || "",
          status: group.status || "pending",
          tone: group.tone || "neutral",
          statusLabel: group.status_label || "",
        };
      });

      if (!this.readOnly || !this.inspectable) return cards;

      return filterExistingEdoAdditionalTitleCards({
        cards,
        details: this.additionalTitleDetails,
      });
    },

    activeTitleKey() {
      return this.selectedTitleKey;
    },

    selectedTitle() {
      if (!this.selectedTitleKey) return null;

      const mainGroup = this.normalizedGroups.find(
        (item) => item.key === this.selectedTitleKey
      );
      const additionalGroup = this.normalizedAdditionalGroups.find(
        (item) => item.key === this.selectedTitleKey
      );
      const group = mainGroup || additionalGroup;
      if (!group) return null;

      const details = additionalGroup
        ? this.additionalTitleDetails
        : this.titleDetails;

      return {
        group,
        isAdditional: Boolean(additionalGroup),
        details:
          details.find((item) => item.key === group.key) ||
          this.fallbackTitleDetails(group),
      };
    },

    selectedTitleSteps() {
      if (!this.selectedTitle) return [];

      return this.stepsForGroup(this.selectedTitle.group);
    },

    // Backend уже решил, какие действия доступны на каждом шаге. Frontend
    // только объединяет их для выбранной группы и убирает точные дубликаты.
    selectedTitleActions() {
      if (this.readOnly) return [];

      const actionKeys = new Set();

      return this.selectedTitleSteps
        .flatMap((step) =>
          Array.isArray(step.actions) ? step.actions : []
        )
        .filter((action) => {
          if (!action?.type || !action?.label) return false;

          const key = `${action.type}:${action.label}`;
          if (actionKeys.has(key)) return false;

          actionKeys.add(key);
          return true;
        });
    },

    selectedTitleNavigation() {
      // Дополнительные титулы независимы: кнопки «назад/вперёд» ошибочно
      // представляли бы Т7 и Т8 как обязательную последовательность.
      if (this.selectedTitle?.isAdditional) return null;

      const cards = this.titleCards;
      const index = cards.findIndex(
        (title) => title.key === this.selectedTitleKey
      );
      if (index === -1) return null;

      return {
        previous: cards[index - 1] || null,
        next: cards[index + 1] || null,
        label: `Титул ${index + 1} из ${cards.length}`,
      };
    },

    titleHelpItems() {
      if (this.selectedTitle?.isAdditional) {
        return this.helpItemsFor(
          this.additionalTitleCards,
          this.normalizedAdditionalGroups,
          this.additionalTitleDetails,
          true
        );
      }

      return this.helpItemsFor(
        this.titleCards,
        this.normalizedGroups,
        this.titleDetails
      );
    },
  },

  methods: {
    stepByKey(key) {
      return this.visibleSteps.find((step) => step.key === key);
    },

    stepsForGroup(group) {
      if (Array.isArray(group.steps)) return group.steps;

      return group.stepKeys
        .map((key) => this.stepByKey(key))
        .filter(Boolean);
    },

    participantLabel(group, steps) {
      const participant =
        group.participant ||
        steps.find((step) => step.participant)?.participant;
      return (
        group.participant_label ||
        edoDocumentFlowParticipantTimelineLabel(participant)
      );
    },

    stepAriaLabel(step) {
      return [
        step.label,
        step.current_label,
        edoDocumentFlowParticipantTimelineLabel(step.participant),
        step.description,
      ]
        .filter(Boolean)
        .join(". ");
    },

    groupTooltip(group, steps) {
      const step = this.representativeStep(group, steps);
      if (!step) return {};

      return {
        title: `${group.label} · ${
          step.label || step.short_label || "Этап ЭДО"
        }`,
        // Цвет и подпись берём с группы, чтобы tooltip и сама полоса титула
        // не расходились при комбинации done + blocked/planned.
        tone: group.tone || "neutral",
        statusLabel: group.status_label || "",
        participant:
          edoDocumentFlowParticipantLabel(
            step.participant || group.participant
          ) || group.participant_label,
        description: step.description || "",
      };
    },

    selectTitle(group) {
      if (this.readOnly && !this.inspectable) return;

      if (this.selectedTitleKey === group.key) {
        this.closeInspector();
        return;
      }

      this.openTitle(group);
    },

    openTitle(group) {
      const resolvedGroup =
        [
          ...this.normalizedGroups,
          ...this.normalizedAdditionalGroups,
        ].find((item) => item.key === group.key) || group;
      const steps = this.stepsForGroup(resolvedGroup);
      const selectedStep = this.representativeStep(
        resolvedGroup,
        steps
      );
      if (!selectedStep) return;

      this.selectedTitleKey = resolvedGroup.key;
    },

    // Все типы ЭДО открываются одной панелью. Пока доменный экран не передал
    // содержимое титула, показываем честное пустое состояние из backend flow.
    fallbackTitleDetails(group) {
      const steps = this.stepsForGroup(group);
      const step = this.representativeStep(group, steps);

      return {
        key: group.key,
        label: group.label,
        role:
          group.participant_label || group.participant?.label || "",
        title: step?.label || group.label,
        hasData: false,
        sections: [],
        emptyState: {
          description: step?.description || "",
        },
      };
    },

    representativeStep(group, steps) {
      const backendActiveStep = steps.find(
        (step) => step.key === group.active_step_key
      );
      if (backendActiveStep) return backendActiveStep;

      return (
        ACTIVE_STATUSES.map((status) =>
          steps.find((step) => step.status === status)
        ).find(Boolean) ||
        steps[steps.length - 1] ||
        null
      );
    },

    statusVariant(tone) {
      return tone === "neutral" ? "default" : tone;
    },

    helpItemsFor(cards, groups, detailsList, preferDetails = false) {
      return cards.map((title) => {
        const group = groups.find((item) => item.key === title.key);
        const details = detailsList.find(
          (item) => item.key === title.key
        );
        const step = this.representativeStep(
          group,
          this.stepsForGroup(group)
        );
        const guide = {
          ...this.fallbackGuide,
          ...(this.guides[step?.key] || {}),
        };

        return {
          key: title.key,
          label: group.label || details?.label,
          role:
            group.participant_label ||
            group.participant?.label ||
            details?.role ||
            "",
          statusLabel: group.status_label || "",
          statusVariant: this.statusVariant(group.tone || "neutral"),
          summary: preferDetails
            ? details?.description ||
              guide.summary ||
              step?.description ||
              ""
            : guide.summary ||
              details?.description ||
              step?.description ||
              "",
          payload: guide.payload || "",
          next: guide.next || "",
          note: guide.note || "",
          participant:
            edoDocumentFlowParticipantLabel(
              group.participant || step?.participant
            ) ||
            group.participant_label ||
            "",
        };
      });
    },

    emitTitleAction(action) {
      if (this.readOnly) return;

      this.$emit("title-action", {
        title: this.selectedTitle.details,
        group: this.selectedTitle.group,
        action,
      });
    },

    setAdditionalTitlesOpen(open) {
      this.additionalTitlesOpen = open;

      if (!open && this.selectedTitle?.isAdditional)
        this.closeInspector();
    },

    closeInspector() {
      this.selectedTitleKey = null;
    },
  },
};
</script>

<style scoped>
.edo-document-flow-details {
  display: grid;
  gap: 12px;
}

.edo-document-flow-details__section {
  display: grid;
  gap: 0;
}
</style>
